import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { SafeHaptics } from '@euler/utils';
import { memo, useCallback } from 'react';
import { Text, TextInput, View, ViewProps } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useTheme } from 'styled-components';

const kSize = 20;
const kScale = 3;
const kPadding = 16;

const AnimatedTextInput = Animated.createAnimatedComponent(TextInput);

export const AlphabetIndex = memo(
  ({
    indics,
    onChange,
    top,
    right,
    ...props
  }: {
    indics: string[];
    onChange?: (index: number) => void;
    /** Override the vertical offset from the parent's top edge. Applied to
     * both the alphabet column and the floating active-letter overlay so they
     * stay aligned. Default matches the previous hard-coded `rpx(20)`. */
    top?: number;
    /** Override the horizontal offset from the parent's right edge.
     * Defaults to 0. Negative values push the bar past the right edge,
     * which is useful when the parent reserves more space than the bar's
     * touch-area padding needs. */
    right?: number;
  } & ViewProps) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const padding = rpx(kPadding);
    const size = rpx(kSize);
    const columnTop = top ?? rpx(20);
    const columnRight = right ?? 0;
    // The floating active-letter overlay was historically `padding + rpx(4)`
    // (= rpx(20) when padding = rpx(16)) — visually identical to the column
    // top. Keep them in sync as the column moves.
    const overlayTop = columnTop;

    const activeIndex = useSharedValue(-1);
    const activeStyle = useAnimatedStyle(() =>
      activeIndex.value < 0 || activeIndex.value >= indics.length
        ? {
            opacity: 0,
          }
        : {
            opacity: 1,
            transform: [
              {
                translateY: activeIndex.value * size,
              },
            ],
          },
    );

    const activeLetterProps = useAnimatedProps(() => {
      return { text: indics[activeIndex.value] ?? '' } as any;
    });
    const activeLetterStyle = useAnimatedStyle(() =>
      activeIndex.value < 0 || activeIndex.value >= indics.length
        ? {
            opacity: 0,
          }
        : {
            opacity: 1,
            transform: [
              {
                translateY: activeIndex.value * size,
              },
            ],
          },
    );

    const indexByOffsetY = (y: number) => {
      'worklet';
      return Math.round((y - padding) / size);
    };

    const notify = useCallback(
      (index: number) => {
        if (index >= 0 && index < indics.length) {
          SafeHaptics.impact();
          onChange?.(index);
        }
      },
      [indics.length, onChange],
    );

    const gesture = Gesture.Pan()
      .onBegin(e => {
        const index = indexByOffsetY(e.y);
        if (index !== activeIndex.value) {
          runOnJS(notify)(index);
        }
        activeIndex.value = index;
      })
      .onUpdate(e => {
        const index = indexByOffsetY(e.y);
        if (index !== activeIndex.value) {
          runOnJS(notify)(index);
        }
        activeIndex.value = index;
      })
      .onEnd(() => {
        activeIndex.value = -1;
      });

    return (
      <>
        <GestureDetector gesture={gesture}>
          <Animated.View
            css={`
              position: absolute;
              top: ${columnTop}px;
              right: ${columnRight}px;
              padding: ${padding}px;
            `}
            {...props}
          >
            <Animated.View
              css={`
                position: absolute;
                left: ${padding}px;
                top: ${padding}px;
                width: ${size}px;
                height: ${size}px;
                border-radius: ${size / 2}px;
                background-color: ${theme.components.table.index
                  .activeBackgroundColor};
              `}
              style={activeStyle}
            />
            {indics.map(letter => (
              <View
                key={letter}
                css={`
                  width: ${size}px;
                  height: ${size}px;
                  align-items: center;
                  justify-content: center;
                `}
              >
                <Text
                  css={`
                    font-family: ${FontFamily.NotoSans.Regular};
                    font-size: ${rpx(12)}px;
                    color: ${theme.components.table.index.color};
                  `}
                >
                  {letter}
                </Text>
              </View>
            ))}
          </Animated.View>
        </GestureDetector>
        <View
          css={`
            position: absolute;
            top: ${overlayTop}px;
            right: 0;
            padding: ${padding}px;
          `}
          pointerEvents="none"
        >
          <Animated.View
            css={`
              width: ${size * kScale}px;
              height: ${size * kScale}px;
              align-items: center;
              justify-content: center;
              left: ${-size * 2}px;
              top: ${-((kScale - 1) * size) / 2}px;
            `}
            style={activeLetterStyle}
          >
            <AnimatedTextInput
              css={`
                font-family: ${FontFamily.NotoSans.Regular};
                font-size: ${rpx(12) * kScale}px;
                color: ${theme.components.table.index.color};
                padding: 0;
              `}
              editable={false}
              showSoftInputOnFocus={false}
              pointerEvents={'none'}
              textAlign="center"
              animatedProps={activeLetterProps}
            />
          </Animated.View>
        </View>
      </>
    );
  },
);
